import React, { useState, useCallback } from "react";
import { Copy, Check, ChevronDown, ChevronRight, Zap, ExternalLink, Home } from "lucide-react";
import { SiMeta, SiGoogle, SiYoutube, SiWhatsapp } from "@icons-pack/react-simple-icons";

import GupshupCard from "src/features/integrations/components/GupshupCard";
import PairEngineCard from "src/features/integrations/components/PairEngineCard";
import ProviderCard from "src/features/integrations/components/ProviderCard";
import RoutingCard from "src/features/integrations/components/RoutingCard";

/* ─── Custom LinkedIn icon (brand icons removed from both lucide & simple-icons) */
function LinkedinIcon({ color = "#0A66C2", size = 24, ...props }) {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      viewBox="0 0 24 24"
      width={size}
      height={size}
      fill={color}
      {...props}
    >
      <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433a2.062 2.062 0 0 1-2.063-2.065 2.064 2.064 0 1 1 2.063 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z" />
    </svg>
  );
}

/* ─── Platform config ────────────────────────────────────────────────────────── */
const PLATFORMS = [
  {
    id: "meta",
    name: "Meta (Facebook + Instagram)",
    platform: "meta",
    color: "#1877F2",
    bgGradient: "from-blue-500/10 to-blue-600/5",
    borderHover: "hover:border-blue-300",
    description: "Capture leads from Facebook and Instagram Lead Ads via webhooks.",
    webhookPath: "/api/v1/webhooks/meta",
    verifyPath: "/api/v1/webhooks/meta",
    setupSteps: [
      "Go to Meta Business Suite → Leads Center → CRM Setup",
      "Select 'Connect CRM' and choose Webhook integration",
      "Paste the Webhook URL shown below",
      "Enter the Verify Token from your environment variables",
      "Subscribe to 'leadgen' field under your Page",
    ],
    Icon: SiMeta,
  },
  {
    id: "google",
    name: "Google Ads",
    platform: "google",
    color: "#4285F4",
    bgGradient: "from-sky-500/10 to-sky-600/5",
    borderHover: "hover:border-sky-300",
    description: "Capture leads from Google Ads Lead Form Extensions via webhooks.",
    webhookPath: "/api/v1/webhooks/google",
    setupSteps: [
      "In Google Ads, go to Tools & Settings → Lead Forms",
      "Create or edit a Lead Form Extension",
      "Under Webhook integration, paste the URL below",
      "Set the webhook secret in your environment variables",
      "Enable the webhook and save",
    ],
    Icon: SiGoogle,
  },
  {
    id: "youtube",
    name: "YouTube",
    platform: "youtube",
    color: "#FF0000",
    bgGradient: "from-red-500/10 to-red-600/5",
    borderHover: "hover:border-red-300",
    description: "Capture leads from YouTube Lead Form Ads (via Google Ads API).",
    webhookPath: "/api/v1/webhooks/google",
    setupSteps: [
      "YouTube Lead Forms use the same Google Ads webhook",
      "Create a YouTube video ad campaign with Lead Form extension",
      "The webhook URL is the same as Google Ads (shared pipeline)",
      "Leads will be automatically tagged as 'youtube' platform",
    ],
    Icon: SiYoutube,
  },
  {
    id: "linkedin",
    name: "LinkedIn",
    platform: "linkedin",
    color: "#0A66C2",
    bgGradient: "from-blue-600/10 to-blue-700/5",
    borderHover: "hover:border-blue-400",
    description: "Capture leads from LinkedIn Lead Gen Forms via webhook.",
    webhookPath: "/api/v1/webhooks/linkedin",
    setupSteps: [
      "Go to LinkedIn Campaign Manager → Account Assets → Lead Gen Forms",
      "Navigate to your Lead Gen Form settings",
      "Under Webhook integration, paste the URL below",
      "Copy the webhook secret and add to your environment variables",
    ],
    Icon: LinkedinIcon,
  },
  {
    id: "whatsapp",
    name: "WhatsApp Business",
    platform: "whatsapp",
    color: "#25D366",
    bgGradient: "from-green-500/10 to-green-600/5",
    borderHover: "hover:border-green-300",
    description: "Receive WhatsApp messages as leads via Meta Cloud API webhooks.",
    webhookPath: "/api/v1/webhooks/whatsapp",
    verifyPath: "/api/v1/webhooks/whatsapp",
    setupSteps: [
      "Go to Meta for Developers → WhatsApp → Configuration",
      "Under Webhooks, click 'Edit' and paste the Webhook URL below",
      "Enter the Verify Token from your environment variables",
      "Subscribe to 'messages' webhook field",
      "Incoming messages from new numbers will auto-create leads",
    ],
    Icon: SiWhatsapp,
  },
  {
    id: "portals",
    name: "Property Portals",
    platform: "portals",
    color: "#FF6B35",
    bgGradient: "from-orange-500/10 to-orange-600/5",
    borderHover: "hover:border-orange-300",
    description: "Auto-parse leads from 99acres, MagicBricks, and Housing.com via email forwarding.",
    webhookPath: "/api/v1/webhooks/portals",
    setupSteps: [
      "Set up email forwarding on your portal notification emails",
      "Forward emails from 99acres, MagicBricks, or Housing.com to your inbound address",
      "Leads are automatically parsed from the email body using pattern matching",
      "Supported portals: 99acres.com, magicbricks.com, housing.com",
    ],
    Icon: Home,
  },
];

/* ─── Clipboard helper ──────────────────────────────────────────────────────── */
function CopyButton({ text }) {
  const [copied, setCopied] = useState(false);

  const handleCopy = useCallback(() => {
    navigator.clipboard.writeText(text).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  }, [text]);

  return (
    <button
      onClick={handleCopy}
      className="inline-flex items-center gap-2 rounded-lg border border-slate-200 bg-white px-3 py-1.5 text-xs font-medium text-slate-600 transition-all hover:border-slate-300 hover:bg-slate-50 hover:text-slate-900 active:scale-95"
    >
      {copied ? (
        <><Check className="h-3.5 w-3.5 text-green-600" /> Copied</>
      ) : (
        <><Copy className="h-3.5 w-3.5" /> Copy</>
      )}
    </button>
  );
}

/* ─── Setup accordion ───────────────────────────────────────────────────────── */
function SetupAccordion({ steps }) {
  const [open, setOpen] = useState(false);

  return (
    <div className="mt-4 rounded-xl border border-slate-100 bg-slate-50/50">
      <button
        onClick={() => setOpen(!open)}
        className="flex w-full items-center justify-between px-4 py-3 text-left text-sm font-medium text-slate-700 transition-colors hover:text-slate-900"
      >
        <span className="flex items-center gap-2">
          <ExternalLink className="h-4 w-4 text-slate-400" />
          Setup Instructions
        </span>
        {open ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
      </button>
      {open && (
        <div className="border-t border-slate-100 px-4 pb-4 pt-2">
          <ol className="space-y-2">
            {steps.map((step, i) => (
              <li key={i} className="flex gap-3 text-sm text-slate-600">
                <span className="mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-slate-200 text-[10px] font-bold text-slate-600">
                  {i + 1}
                </span>
                {step}
              </li>
            ))}
          </ol>
        </div>
      )}
    </div>
  );
}

/* ─── Test webhook button ───────────────────────────────────────────────────── */
function TestWebhookButton({ platform }) {
  const [testing, setTesting] = useState(false);
  const [result, setResult] = useState(null);

  const handleTest = useCallback(async () => {
    setTesting(true);
    setResult(null);
    try {
      const res = await fetch(`/api/v1/webhooks/${platform}/test`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
      });
      const data = await res.json().catch(() => ({}));
      // The endpoint now reports whether a Lead was actually created, not just
      // that the job was enqueued.
      if (res.ok && data.status === "lead_created") {
        setResult({ kind: "success", label: `✓ Lead created${data.name ? `: ${data.name}` : ""}` });
      } else if (res.ok && data.status === "duplicate") {
        setResult({ kind: "success", label: "✓ Worked (duplicate skipped)", title: data.reason });
      } else {
        setResult({ kind: "error", label: "✗ No lead created", title: data.reason });
      }
    } catch {
      setResult({ kind: "error", label: "✗ Failed" });
    } finally {
      setTesting(false);
      setTimeout(() => setResult(null), 5000);
    }
  }, [platform]);

  return (
    <button
      onClick={handleTest}
      disabled={testing}
      title={result?.title || undefined}
      className="inline-flex items-center gap-2 rounded-lg border border-slate-200 bg-white px-3 py-1.5 text-xs font-medium text-slate-600 transition-all hover:border-slate-300 hover:bg-slate-50 hover:text-slate-900 disabled:opacity-50 active:scale-95"
    >
      <Zap className={`h-3.5 w-3.5 ${testing ? "animate-pulse text-amber-500" : ""}`} />
      {testing ? "Testing…" : result ? result.label : "Send Test Lead"}
    </button>
  );
}

/* ─── Platform Card ─────────────────────────────────────────────────────────── */
function PlatformCard({ platform }) {
  const baseUrl = typeof window !== "undefined" ? window.location.origin : "";
  const webhookUrl = `${baseUrl}${platform.webhookPath}`;

  return (
    <div
      className={`group relative overflow-hidden rounded-2xl border border-slate-200 bg-gradient-to-br ${platform.bgGradient} p-6 shadow-sm transition-all duration-300 hover:-translate-y-0.5 hover:shadow-lg hover:shadow-slate-200/50 ${platform.borderHover}`}
    >
      {/* Header */}
      <div className="flex items-start justify-between">
        <div className="flex items-center gap-3">
          <div className="flex h-12 w-12 items-center justify-center rounded-xl border border-slate-100 bg-white shadow-sm overflow-hidden">
            <platform.Icon color={platform.color} size={26} strokeWidth={platform.id === 'portals' ? 2 : undefined} />
          </div>
          <div>
            <h3 className="font-semibold text-slate-900">{platform.name}</h3>
            <span className="inline-flex items-center gap-1 rounded-full bg-slate-100 px-2 py-0.5 text-[10px] font-medium text-slate-500">
              <span className="h-1.5 w-1.5 rounded-full bg-amber-400" />
              Not configured
            </span>
          </div>
        </div>
      </div>

      {/* Description */}
      <p className="mt-3 text-sm leading-relaxed text-slate-500">
        {platform.description}
      </p>

      {/* Webhook URL */}
      <div className="mt-4 space-y-1.5">
        <label className="text-xs font-medium text-slate-500">Webhook URL</label>
        <div className="flex items-center gap-2">
          <div className="flex-1 truncate rounded-lg border border-slate-200 bg-white px-3 py-2 font-mono text-xs text-slate-600">
            {webhookUrl}
          </div>
          <CopyButton text={webhookUrl} />
        </div>
      </div>

      {/* Setup instructions */}
      <SetupAccordion steps={platform.setupSteps} />

      {/* Actions */}
      <div className="mt-4 flex items-center gap-2">
        <TestWebhookButton platform={platform.platform} />
      </div>
    </div>
  );
}

/* ─── Main Page ─────────────────────────────────────────────────────────────── */
export default function IntegrationsPage() {
  return (
    <div className="space-y-8">
      <div>
        <h2 className="text-xl font-bold tracking-tight text-slate-900">Integrations</h2>
      </div>

      <section className="space-y-4">
        <div>
          <h3 className="text-sm font-semibold text-slate-900">Messaging providers</h3>
          <p className="text-sm text-slate-500">
            Send WhatsApp, SMS, and email to leads from inside the CRM — all logged on the timeline.
          </p>
        </div>
        <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
          <GupshupCard />
          <ProviderCard provider="exotel" />
          <ProviderCard provider="msg91" />
          <ProviderCard provider="springedge" />
          <RoutingCard />
          <PairEngineCard />
        </div>
      </section>

      <section className="space-y-4">
        <div>
          <h3 className="text-sm font-semibold text-slate-900">Lead sources</h3>
          <p className="text-sm text-slate-500">
            Connect your lead sources to automatically ingest and score leads in real time.
          </p>
        </div>
        <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
          {PLATFORMS.map((platform) => (
            <PlatformCard key={platform.id} platform={platform} />
          ))}
        </div>
      </section>
    </div>
  );
}
